import React, { useState } from 'react';
import { Modal, Button, Alert, Spinner } from 'react-bootstrap';
import { FileCheck2, Download } from 'lucide-react';
import { Link } from 'react-router-dom';
import useAuthHeaders from '../hooks/useAuthHeaders';
import { CREDENCIAL_LABEL, describirResultado, descargarConstanciaEliminacion } from '../utils/constanciasEliminacion';

/**
 * Confirmación que se muestra después de eliminar una credencial o un RFC (AB#763): el folio de cada
 * constancia emitida y su botón de descarga. Si la eliminación falló, `error` trae el motivo y solo se
 * listan las constancias de lo que sí se completó antes del fallo (la que falló no tiene constancia).
 */
const ConstanciaEliminacionAviso = ({ show, onHide, titulo, constancias = [], error = null, advertencia = null }) => {
  const { getAuthHeaders } = useAuthHeaders();
  const [descargando, setDescargando] = useState(null);
  const [errorDescarga, setErrorDescarga] = useState(null);

  const descargar = async (folio) => {
    setDescargando(folio);
    setErrorDescarga(null);
    try {
      await descargarConstanciaEliminacion(folio, getAuthHeaders);
    } catch (err) {
      setErrorDescarga(err.message);
    } finally {
      setDescargando(null);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-2" style={{ fontSize: '1.05rem' }}>
          <FileCheck2 size={20} color={error ? '#dc3545' : '#1d7a45'} />
          {titulo}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="danger" className="mb-3">
            {error}
            <div className="mt-1 small">La credencial sigue vigente, así que no se emitió constancia por ella. Intenta de nuevo en unos minutos.</div>
          </Alert>
        )}

        {constancias.length > 0 && (
          <>
            <p className="mb-2 small text-muted">
              {error
                ? 'Antes del error sí se eliminaron estas credenciales; su constancia es válida:'
                : 'Se emitió una constancia por cada credencial. Guárdala para acreditar que la custodia terminó.'}
            </p>
            <div className="d-grid gap-2">
              {constancias.map((c) => (
                <div key={c.folio} className="d-flex align-items-center justify-content-between gap-2 border rounded px-3 py-2">
                  <div style={{ minWidth: 0 }}>
                    <div className="fw-semibold">{CREDENCIAL_LABEL[c.credencial] || c.credencial} · {describirResultado(c)}</div>
                    <code style={{ fontSize: '.85rem' }}>{c.folio}</code>
                  </div>
                  <Button size="sm" variant="outline-primary" onClick={() => descargar(c.folio)} disabled={descargando === c.folio}
                    className="d-flex align-items-center gap-1 flex-shrink-0">
                    {descargando === c.folio ? <Spinner animation="border" size="sm" /> : <Download size={14} />}
                    Descargar constancia
                  </Button>
                </div>
              ))}
            </div>
          </>
        )}

        {advertencia && <Alert variant="warning" className="mt-3 mb-0 small">{advertencia}</Alert>}
        {errorDescarga && <Alert variant="danger" className="mt-3 mb-0 small">{errorDescarga}</Alert>}

        {constancias.length > 0 && (
          <p className="mt-3 mb-0 small text-muted">
            También puedes consultarlas después en <Link to="/constancias-eliminacion" onClick={onHide}>Constancias de eliminación</Link>.
          </p>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={onHide}>Cerrar</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ConstanciaEliminacionAviso;
